'use client'
import * as React from 'react'

// Small client-side logo with a graceful fallback. A resolved logo src can
// still fail at runtime (a missing local asset, a Simple Icons / Brandfetch
// CDN hiccup), which left empty tiles. When the image is missing or errors we
// render `fallback` (a wordmark, say) or the brand initial, so every tile
// always reads as a real brand.
export function PlatformLogo({
  src,
  mobileSrc,
  name,
  size = 26,
  className,
  fallback,
}: {
  src: string | null

  mobileSrc?: string | null
  name: string
  size?: number
  /** Class applied to the <img>. When set, overrides the default size box. */
  className?: string
  /** Rendered when there's no src or the image fails. Defaults to the initial. */
  fallback?: React.ReactNode
}) {
  const [failed, setFailed] = React.useState(false)
  const [mobileFailed, setMobileFailed] = React.useState(false)

  const imgProps = className
    ? { className }
    : {
        width: size,
        height: size,
        style: { height: size, width: size },
        className: 'object-contain',
      }

  if (!src || failed) {
    return (
      <>
        {fallback ?? <span className="font-display text-base font-medium">{name.charAt(0)}</span>}
      </>
    )
  }

  if (mobileSrc && !mobileFailed) {
    return (
      <>
        {/* Mobile-only compact logo */}

        <img
          src={mobileSrc}
          alt={`${name} logo`}
          loading="lazy"
          onError={() => setMobileFailed(true)}
          {...imgProps}
          className={`${imgProps.className ?? ''} sm:hidden`.trim()}
        />
        {/* Desktop logo (sm and above) */}

        <img
          src={src}
          alt={`${name} logo`}
          loading="lazy"
          onError={() => setFailed(true)}
          {...imgProps}
          className={`${imgProps.className ?? ''} hidden sm:block`.trim()}
        />
      </>
    )
  }

  return (
    /* eslint-disable-next-line @next/next/no-img-element */
    <img
      src={src}
      alt={`${name} logo`}
      loading="lazy"
      onError={() => setFailed(true)}
      {...imgProps}
    />
  )
}
